import React, { useState, useMemo, useEffect, useRef } from 'react';
import { budgetSettingsApi } from '../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Orçamentos predefinidos por categoria (em GBP)
const DEFAULT_BUDGETS = {
  'FOOD': 300,
  'GO OUT': 300,
  'BILLS': 2492.1,
  'AMANDA': 100,
  'MARTIN': 100,
  'HOLIDAYS': 150,
  'HOUSE': 30,
  'OTHERS': 100
};

// Valores padrão para dinheiro economizado
const DEFAULT_SAVINGS = {
  'MARTIN': 0,
  'AMANDA': 0
};

const CATEGORY_COLORS = {
  'FOOD': '#10B981',      // Verde
  'GO OUT': '#F59E0B',    // Amarillo
  'BILLS': '#EF4444',     // Rojo
  'AMANDA': '#EC4899',    // Rosa
  'MARTIN': '#3B82F6',    // Azul
  'HOLIDAYS': '#8B5CF6',  // Púrpura
  'HOUSE': '#6B7280',     // Gris
  'OTHERS': '#F97316'     // Naranja
};

const BudgetCharts = ({ transactions }) => {
  const [budgets, setBudgets] = useState(() => {
    const saved = localStorage.getItem('saver-budgets');
    return saved ? JSON.parse(saved) : DEFAULT_BUDGETS;
  });

  const [savings, setSavings] = useState(() => {
    const saved = localStorage.getItem('saver-savings');
    return saved ? JSON.parse(saved) : DEFAULT_SAVINGS;
  });

  const [editingBudgets, setEditingBudgets] = useState(false);
  const [guideExpanded, setGuideExpanded] = useState(false);
  const [tempBudgets, setTempBudgets] = useState({});
  const [tempSavings, setTempSavings] = useState({});
  const isFirstLoad = useRef(true);

  // Cargar configuración desde el servidor al montar
  useEffect(() => {
    budgetSettingsApi.get().then(settings => {
      if (settings) {
        if (settings.budgets) {
          setBudgets(settings.budgets);
          localStorage.setItem('saver-budgets', JSON.stringify(settings.budgets));
        }
        if (settings.savings) {
          setSavings(settings.savings);
          localStorage.setItem('saver-savings', JSON.stringify(settings.savings));
        }
      }
      isFirstLoad.current = false;
    }).catch(() => {
      isFirstLoad.current = false;
    });
  }, []);

  // Guardar en servidor y localStorage cuando cambian (debounced, skip primera carga)
  const saveTimeoutRef = useRef(null);
  useEffect(() => {
    if (isFirstLoad.current) return;
    localStorage.setItem('saver-budgets', JSON.stringify(budgets));
    localStorage.setItem('saver-savings', JSON.stringify(savings));
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      budgetSettingsApi.update(budgets, savings).catch(console.error);
    }, 1000);
  }, [budgets, savings]);

  // Formatear moneda británica
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  // Calcular días restantes del mes
  const getDaysRemaining = () => {
    const today = new Date();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const daysRemaining = lastDayOfMonth.getDate() - today.getDate();
    return Math.max(0, daysRemaining);
  };

  const daysRemaining = getDaysRemaining();

  // Calcular gastos por categoría - solo del mes actual
  const expensesByCategory = useMemo(() => {
    const expenses = {};
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    transactions.forEach(transaction => {
      const transactionDate = new Date(transaction.date);
      const transactionYear = transactionDate.getFullYear();
      const transactionMonth = transactionDate.getMonth();
      
      // Solo incluir transacciones del mes y año actual
      if (transactionYear === currentYear && transactionMonth === currentMonth) {
        const category = transaction.expenseType;
        if (!expenses[category]) {
          expenses[category] = 0;
        }
        expenses[category] += transaction.gbpAmount;
      }
    });
    
    return expenses;
  }, [transactions]);

  // Preparar datos para gráficos
  const chartData = useMemo(() => {
    const data = {};
    
    Object.keys(budgets).forEach(category => {
      const budget = budgets[category];
      const spent = expensesByCategory[category] || 0;
      const remaining = Math.max(0, budget - spent);
      const overspent = Math.max(0, spent - budget);
      
      // Calcular disponible por día para FOOD y GO OUT
      let availablePerDay = null;
      let dailyBudgetStatus = null;
      
      if ((category === 'FOOD' || category === 'GO OUT') && remaining > 0 && daysRemaining > 0) {
        availablePerDay = remaining / daysRemaining;
        dailyBudgetStatus = availablePerDay >= 10 ? 'good' : 'low';
      }
      
      data[category] = {
        category,
        budget,
        spent,
        remaining,
        overspent,
        availablePerDay,
        dailyBudgetStatus,
        percentage: budget > 0 ? (spent / budget) * 100 : 0,
        pieData: overspent > 0 ? [
          { name: 'Gasto', value: budget, fill: CATEGORY_COLORS[category] },
          { name: 'Ultrapassado', value: overspent, fill: '#DC2626' }
        ] : [
          { name: 'Gasto', value: spent, fill: CATEGORY_COLORS[category] },
          { name: 'Disponível', value: remaining, fill: '#E5E7EB' }
        ]
      };
    });
    
    return data;
  }, [budgets, expensesByCategory, daysRemaining]);

  // Função para avaliar expressões matemáticas de forma segura
  const evaluateExpression = (expression) => {
    try {
      // Remove espaços e verifica se contém operadores matemáticos
      const cleanExpression = expression.toString().replace(/\s/g, '');
      
      // Verifica se tem operadores matemáticos
      if (!/[+\-*/]/.test(cleanExpression)) {
        return parseFloat(cleanExpression) || 0;
      }
      
      // Validação de segurança: só permite números, pontos e operadores básicos
      if (!/^[0-9+\-*/.() ]+$/.test(cleanExpression)) {
        return parseFloat(expression) || 0;
      }
      
      // Avalia a expressão de forma segura
      const result = Function('"use strict"; return (' + cleanExpression + ')')();
      
      // Verifica se o resultado é um número válido
      if (isNaN(result) || !isFinite(result)) {
        return parseFloat(expression) || 0;
      }
      
      return parseFloat(result.toFixed(2));
    } catch (error) {
      // Se houver erro, retorna o valor original parseado
      return parseFloat(expression) || 0;
    }
  };

  const handleBudgetInputChange = (category, value) => {
    setTempBudgets(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleBudgetBlur = (category) => {
    const inputValue = tempBudgets[category] !== undefined ? tempBudgets[category] : budgets[category];
    const calculatedValue = evaluateExpression(inputValue);
    setBudgets(prev => ({
      ...prev,
      [category]: calculatedValue
    }));
    // Limpa o estado temporário
    setTempBudgets(prev => {
      const newTemp = { ...prev };
      delete newTemp[category];
      return newTemp;
    });
  };

  const handleCloseMonth = () => {
    const martinNet = (budgets['MARTIN'] || 0) - (expensesByCategory['MARTIN'] || 0);
    const amandaNet = (budgets['AMANDA'] || 0) - (expensesByCategory['AMANDA'] || 0);
    const newMartinSavings = Math.max(0, (savings['MARTIN'] || 0) + martinNet);
    const newAmandaSavings = Math.max(0, (savings['AMANDA'] || 0) + amandaNet);
    const msg = `Fechar mês e aplicar ajuste de poupança?\n\nMartin: ${martinNet >= 0 ? '+' : ''}${formatCurrency(martinNet)} → Total: ${formatCurrency(newMartinSavings)}\nAmanda: ${amandaNet >= 0 ? '+' : ''}${formatCurrency(amandaNet)} → Total: ${formatCurrency(newAmandaSavings)}`;
    if (window.confirm(msg)) {
      setSavings({ MARTIN: newMartinSavings, AMANDA: newAmandaSavings });
    }
  };

  const handleSavingsInputChange = (person, value) => {
    setTempSavings(prev => ({
      ...prev,
      [person]: value
    }));
  };

  const handleSavingsBlur = (person) => {
    const inputValue = tempSavings[person] !== undefined ? tempSavings[person] : savings[person];
    const calculatedValue = evaluateExpression(inputValue);
    setSavings(prev => ({
      ...prev,
      [person]: calculatedValue
    }));
    // Limpa o estado temporário
    setTempSavings(prev => {
      const newTemp = { ...prev };
      delete newTemp[person];
      return newTemp;
    });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="expense-card-dark">
          <p className="font-medium text-white">{data.name}</p>
          <p className="text-sm text-gray-300">
            {formatCurrency(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Solo calcular totales para categorías que tienen gastos reales
  const categoriesWithExpenses = Object.keys(expensesByCategory).filter(category => expensesByCategory[category] > 0);
  const totalBudget = categoriesWithExpenses.reduce((sum, category) => sum + (budgets[category] || 0), 0);
  const totalSpent = Object.values(expensesByCategory).reduce((sum, spent) => sum + spent, 0);
  const totalRemaining = Math.max(0, totalBudget - totalSpent);

  // Calcular dinheiro economizado real (descontando ultrapassagens)
  const calculateRealSavings = useMemo(() => {
    const martinOverspent = Math.max(0, (expensesByCategory['MARTIN'] || 0) - (budgets['MARTIN'] || 0));
    const amandaOverspent = Math.max(0, (expensesByCategory['AMANDA'] || 0) - (budgets['AMANDA'] || 0));
    return {
      MARTIN: (savings['MARTIN'] || 0) - martinOverspent,
      AMANDA: (savings['AMANDA'] || 0) - amandaOverspent,
      martinOverspent,
      amandaOverspent
    };
  }, [savings, expensesByCategory, budgets]);

  return (
    <div className="expense-card">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Orçamentos Mensais por Categoria
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestão mensal do seu dinheiro - Renova todo mês
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCloseMonth}
            className="button-secondary text-sm"
            title="Aplica o saldo do mês (sobra ou déficit) ao dinheiro economizado"
          >
            Fechar Mês
          </button>
          <button
            onClick={() => setEditingBudgets(!editingBudgets)}
            className={editingBudgets ? "button-success" : "button-primary"}
          >
            {editingBudgets ? 'Salvar' : 'Editar'}
          </button>
        </div>
      </div>

      {/* Resumo geral em duas fileiras de 3 retângulos */}
      <div className="mb-6">
        {/* Primeira fileira */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-800 text-center hover-lift p-4 rounded-xl shadow-lg">
            <div className="text-2xl font-bold text-white mb-1">
              {formatCurrency(totalBudget)}
            </div>
            <div className="text-sm text-gray-300">Orçamento Mensal</div>
            <div className="w-full h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mt-2"></div>
          </div>
          <div className="bg-gray-800 text-center hover-lift p-4 rounded-xl shadow-lg">
            <div className="text-2xl font-bold text-white mb-1">
              {formatCurrency(totalSpent)}
            </div>
            <div className="text-sm text-gray-300">Gasto no Mês</div>
            <div className="w-full h-0.5 bg-gradient-to-r from-red-400 to-pink-400 rounded-full mt-2"></div>
          </div>
          <div className="bg-gray-800 text-center hover-lift p-4 rounded-xl shadow-lg">
            <div className="text-2xl font-bold text-white mb-1">
              {formatCurrency(Math.abs(totalRemaining))}
            </div>
            <div className="text-sm text-gray-300">
              {totalRemaining >= 0 ? 'Disponível' : 'Ultrapassado'}
            </div>
            <div className={`w-full h-0.5 rounded-full mt-2 ${
              totalRemaining >= 0 
                ? 'bg-gradient-to-r from-green-400 to-emerald-400' 
                : 'bg-gradient-to-r from-red-400 to-pink-400'
            }`}></div>
          </div>
        </div>
        
        {/* Segunda fileira */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800 text-center hover-lift p-4 rounded-xl shadow-lg">
            <div className="text-2xl font-bold text-white mb-1">
              {daysRemaining}
            </div>
            <div className="text-sm text-gray-300">
              {daysRemaining === 1 ? 'Dia Restante' : 'Dias Restantes'}
            </div>
            <div className="w-full h-0.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full mt-2"></div>
          </div>
          <div className="bg-gray-800 text-center hover-lift p-4 rounded-xl shadow-lg">
            <div className={`text-2xl font-bold mb-1 ${calculateRealSavings.MARTIN < 0 ? 'text-red-400' : 'text-white'}`}>
              {calculateRealSavings.MARTIN < 0 ? '-' : ''}{formatCurrency(Math.abs(calculateRealSavings.MARTIN))}
            </div>
            <div className="text-sm text-gray-300">{calculateRealSavings.MARTIN < 0 ? 'Déficit Martin' : 'Economizado Martin'}</div>
            {calculateRealSavings.martinOverspent > 0 && (
              <div className="text-xs text-red-300 mt-1">
                Poupança: {formatCurrency(savings['MARTIN'] || 0)} | Excesso: -{formatCurrency(calculateRealSavings.martinOverspent)}
              </div>
            )}
            <div className={`w-full h-0.5 rounded-full mt-2 ${calculateRealSavings.MARTIN < 0 ? 'bg-gradient-to-r from-red-400 to-pink-400' : 'bg-gradient-to-r from-blue-400 to-cyan-400'}`}></div>
          </div>
          <div className="bg-gray-800 text-center hover-lift p-4 rounded-xl shadow-lg">
            <div className={`text-2xl font-bold mb-1 ${calculateRealSavings.AMANDA < 0 ? 'text-red-400' : 'text-white'}`}>
              {calculateRealSavings.AMANDA < 0 ? '-' : ''}{formatCurrency(Math.abs(calculateRealSavings.AMANDA))}
            </div>
            <div className="text-sm text-gray-300">{calculateRealSavings.AMANDA < 0 ? 'Déficit Amanda' : 'Economizado Amanda'}</div>
            {calculateRealSavings.amandaOverspent > 0 && (
              <div className="text-xs text-red-300 mt-1">
                Poupança: {formatCurrency(savings['AMANDA'] || 0)} | Excesso: -{formatCurrency(calculateRealSavings.amandaOverspent)}
              </div>
            )}
            <div className={`w-full h-0.5 rounded-full mt-2 ${calculateRealSavings.AMANDA < 0 ? 'bg-gradient-to-r from-red-400 to-pink-400' : 'bg-gradient-to-r from-pink-400 to-purple-400'}`}></div>
          </div>
        </div>
      </div>

      {/* Cards de categorías clave */}
      {(() => {
        const KEY_CATEGORIES = [
          { key: 'FOOD',    label: 'Food',    gradient: 'from-green-400 to-emerald-400',  icon: '' },
          { key: 'GO OUT',  label: 'Go Out',  gradient: 'from-yellow-400 to-amber-400',   icon: '' },
          { key: 'BILLS',   label: 'Bills',   gradient: 'from-red-400 to-pink-400',       icon: '' },
          { key: 'AMANDA',  label: 'Amanda',  gradient: 'from-pink-400 to-purple-400',    icon: '' },
          { key: 'MARTIN',  label: 'Martin',  gradient: 'from-blue-400 to-cyan-400',      icon: '' },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {KEY_CATEGORIES.map(({ key, label, gradient, icon }) => {
              const budget = budgets[key] || 0;
              const spent = expensesByCategory[key] || 0;
              const remaining = budget - spent;
              const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
              const over = remaining < 0;
              return (
                <div key={key} className="bg-gray-800 rounded-xl p-3 shadow-lg hover-lift text-center">
                  <div className="text-xs text-gray-400 mb-1">{label}</div>
                  <div className={`text-xl font-bold ${over ? 'text-red-400' : 'text-white'}`}>
                    {formatCurrency(Math.abs(remaining))}
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    {over ? 'ultrapassado' : `de ${formatCurrency(budget)}`}
                  </div>
                  <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${over ? 'from-red-500 to-pink-500' : gradient}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{Math.round(pct)}% usado</div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Editor de presupuestos */}
      {editingBudgets && (
        <div className="expense-card-dark mb-8 fade-in">
          <h3 className="text-lg font-bold text-white mb-2 flex items-center">
            Editar Orçamentos
          </h3>
          <div className="mb-6 p-3 bg-blue-900/30 rounded-lg border border-blue-500/30">
            <div className="text-sm text-blue-200 flex items-center">
              <strong>Dica:</strong> Você pode usar expressões matemáticas nos campos! 
              <span className="ml-2 text-blue-300">Ex: 100+50, 200*2, 500-100, 400/2</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.keys(budgets).map(category => (
              <div key={category} className="space-y-2">
                <label className="text-sm font-medium text-gray-300 flex items-center">
                  <span className="w-2 h-2 rounded-full mr-2" style={{backgroundColor: CATEGORY_COLORS[category]}}></span>
                  {category}
                </label>
                <input
                  type="text"
                  value={tempBudgets[category] !== undefined ? tempBudgets[category] : budgets[category]}
                  onChange={(e) => handleBudgetInputChange(category, e.target.value)}
                  onBlur={() => handleBudgetBlur(category)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur(); // Força o onBlur quando Enter é pressionado
                    }
                  }}
                  className="input-field"
                  placeholder="100+50 ou 200*2"
                />
              </div>
            ))}
          </div>
          
          {/* Seção para editar dinheiro economizado */}
          <div className="mt-8 pt-6 border-t border-gray-600">
            <h4 className="text-md font-bold text-white mb-4 flex items-center">
              Dinheiro Economizado
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300 flex items-center">
                  <span className="w-2 h-2 rounded-full mr-2 bg-blue-400"></span>
                  MARTIN
                </label>
                <input
                  type="text"
                  value={tempSavings['MARTIN'] !== undefined ? tempSavings['MARTIN'] : savings['MARTIN']}
                  onChange={(e) => handleSavingsInputChange('MARTIN', e.target.value)}
                  onBlur={() => handleSavingsBlur('MARTIN')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur(); // Força o onBlur quando Enter é pressionado
                    }
                  }}
                  className="input-field"
                  placeholder="100+50 ou 200*2"
                />
                {savings['MARTIN'] !== calculateRealSavings.MARTIN && (
                  <div className="text-xs text-red-300">
                    Atual (após desconto): {formatCurrency(calculateRealSavings.MARTIN)}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300 flex items-center">
                  <span className="w-2 h-2 rounded-full mr-2 bg-pink-400"></span>
                  AMANDA
                </label>
                <input
                  type="text"
                  value={tempSavings['AMANDA'] !== undefined ? tempSavings['AMANDA'] : savings['AMANDA']}
                  onChange={(e) => handleSavingsInputChange('AMANDA', e.target.value)}
                  onBlur={() => handleSavingsBlur('AMANDA')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur(); // Força o onBlur quando Enter é pressionado
                    }
                  }}
                  className="input-field"
                  placeholder="100+50 ou 200*2"
                />
                {savings['AMANDA'] !== calculateRealSavings.AMANDA && (
                  <div className="text-xs text-red-300">
                    Atual (após desconto): {formatCurrency(calculateRealSavings.AMANDA)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid de categorías - solo categorías con transacciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {Object.entries(chartData)
          .filter(([category, data]) => data.spent > 0) // Solo mostrar categorías con gastos reales
          .map(([category, data]) => (
          <div key={category} className="expense-card">
            
            {/* Título de la categoría */}
            <div className="text-center mb-3">
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                {category}
              </h3>
              <div className="w-16 h-1 mx-auto rounded-full" style={{background: `linear-gradient(90deg, ${CATEGORY_COLORS[category]}, ${CATEGORY_COLORS[category]}66)`}}></div>
            </div>
            
            {/* Gráfico circular más pequeño */}
            <div className="h-32 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {data.pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Información en grid 2x2 más compacto */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-gray-50 p-2 rounded-lg text-center">
                <div className="text-xs text-gray-600 mb-1">Orçamento Mês</div>
                <div className="font-semibold text-sm text-gray-900">{formatCurrency(data.budget)}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg text-center">
                <div className="text-xs text-gray-600 mb-1">Gasto</div>
                <div className={`font-semibold text-sm ${data.overspent > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatCurrency(data.spent)}
                </div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg text-center">
                <div className="text-xs text-gray-600 mb-1">
                  {data.overspent > 0 ? 'Ultrapassado' : 'Disponível'}
                </div>
                <div className={`font-semibold text-sm ${data.overspent > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(data.overspent > 0 ? data.overspent : data.remaining)}
                </div>
              </div>
              
              {/* Información adicional dependiendo de la categoría */}
              {data.availablePerDay !== null && daysRemaining > 0 ? (
                <div className="bg-gray-50 p-2 rounded-lg text-center">
                  <div className="text-xs text-gray-600 mb-1">Por dia restante</div>
                  <div className={`font-semibold text-sm ${data.dailyBudgetStatus === 'good' ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(data.availablePerDay)}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-2 rounded-lg text-center">
                  <div className="text-xs text-gray-600 mb-1">Uso</div>
                  <div className={`font-semibold text-sm ${
                    data.percentage > 100 ? 'text-red-600' : 
                    data.percentage > 80 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {Math.round(data.percentage)}%
                  </div>
                </div>
              )}
            </div>

            {/* Estados/Alertas más compactas */}
            <div className="space-y-2 mb-3">
              {data.overspent > 0 && (
                <div className="p-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white text-center">
                  <div className="font-medium text-xs">
                    Ultrapassado
                  </div>
                </div>
              )}
              
              {data.dailyBudgetStatus === 'low' && (
                <div className="p-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white text-center">
                  <div className="font-medium text-xs">
                    Orçamento baixo
                  </div>
                </div>
              )}
              
              {data.dailyBudgetStatus === 'good' && (
                <div className="p-2 rounded-lg bg-gradient-to-r from-green-400 to-emerald-500 text-white text-center">
                  <div className="font-medium text-xs">
                    Orçamento saudável
                  </div>
                </div>
              )}
              
              {data.remaining > 0 && data.percentage > 80 && !data.dailyBudgetStatus && (
                <div className="p-2 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-center">
                  <div className="font-medium text-xs">
                    Perto do limite
                  </div>
                </div>
              )}
            </div>

            {/* Barra de progreso más compacta */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm font-semibold ${
                  data.percentage > 100 ? 'text-red-600' : 
                  data.percentage > 80 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {Math.round(data.percentage)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    data.percentage > 100 ? 'bg-gradient-to-r from-red-500 to-pink-500' : 
                    data.percentage > 80 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 
                    'bg-gradient-to-r from-green-400 to-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, data.percentage)}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Guía de Interpretación - Minimizada abajo de todos los gráficos */}
      <div className="expense-card-dark mt-6">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setGuideExpanded(!guideExpanded)}>
          <div className="flex items-center">
            <h3 className="text-lg font-bold text-white flex items-center">
              Guia de Interpretação
            </h3>
            <div className="w-16 h-1 ml-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-400"></div>
          </div>
          <button 
            type="button"
            className="button-secondary text-xl"
          >
            {guideExpanded ? '▼' : '▶'}
          </button>
        </div>
        
        {guideExpanded && (
          <div className="mt-4 space-y-3 text-xs fade-in">
            <div>
              <h4 className="font-semibold text-gray-300 mb-2">Códigos de Cor</h4>
              <div className="space-y-1 text-gray-300">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 mr-2"></div>
                  <span>Verde: Dentro do orçamento</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 mr-2"></div>
                  <span>Amarelo: Perto do limite (&gt;80%)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-pink-500 mr-2"></div>
                  <span>Vermelho: Orçamento ultrapassado</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-300 mb-2">Elementos Visuais</h4>
              <div className="space-y-1 text-gray-300">
                <div>Gráfico circular: Proporção gasta vs disponível no mês</div>
                <div>Barra de progresso: Percentual do orçamento mensal utilizado</div>
                <div>Dias restantes: Dias que restam do mês atual</div>
                <div>Por dia restante: Orçamento disponível dividido por dias restantes (FOOD e GO OUT)</div>
                <div>Os orçamentos se renovam automaticamente a cada mês</div>
              </div>
            </div>
            
            <div className="pt-2 border-t border-gray-600">
              <div className="space-y-1 text-gray-300">
                <div className="flex items-center">
                  <span>Orçamento saudável: ≥£10 por dia</span>
                </div>
                <div className="flex items-center">
                  <span>Orçamento baixo: &lt;£10 por dia</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default BudgetCharts; 